import React, { useState, useCallback } from 'react';
import { thumbUrl, fullUrl } from '../utils/imageUtils';
import styles from './Work.module.css';
import Footer from './Footer';

const GALLERY_IMAGE_COUNT = 35;
const galleryIndices = Array.from({ length: GALLERY_IMAGE_COUNT }, (_, i) => i + 1);

const Work: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(1);
  const modalRef = React.useRef<HTMLDivElement>(null);

  const openModal = useCallback((idx: number) => {
    setCurrentIdx(idx);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const showPrev = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentIdx(idx => (idx === 1 ? GALLERY_IMAGE_COUNT : idx - 1));
  }, []);

  const showNext = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentIdx(idx => (idx === GALLERY_IMAGE_COUNT ? 1 : idx + 1));
  }, []);

  // Keyboard navigation and focus trap for modal accessibility
  React.useEffect(() => {
    if (!modalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
      if (e.key === 'Escape') closeModal();
      // Focus trap: Tab/Shift+Tab
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    // Focus modal on open
    setTimeout(() => {
      modalRef.current?.focus();
    }, 0);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, showPrev, showNext, closeModal]);

  return (
    <>
      <div className={styles.workContainer}>
        <div className={styles.gallery}>
          {galleryIndices.map(i => (
            <img
              key={i}
              src={thumbUrl(i)}
              loading="lazy"
              decoding="async"
              width={800}
              height={550}
              alt={`Tattoo artwork ${i}`}
              onClick={() => openModal(i)}
              className={styles.workImage}
            />
          ))}
        </div>
      </div>
      {modalOpen && (
        <div
          className={styles.modalOverlay}
          onClick={closeModal}
          aria-modal="true"
          role="dialog"
        >
          <div
            className={styles.modalContent}
            onClick={e => e.stopPropagation()}
            ref={modalRef}
            tabIndex={-1}
            aria-label={`Tattoo artwork ${currentIdx} of ${GALLERY_IMAGE_COUNT}`}
          >
            <button className={styles.modalClose} onClick={closeModal} aria-label="Close">&times;</button>
            <div className={styles.modalPagination}>{currentIdx} / {GALLERY_IMAGE_COUNT}</div>
            <div className={styles.modalImageContainer}>
              <button className={styles.modalNavLeft} onClick={showPrev} aria-label="Previous">{'<'}</button>
              <img
                src={fullUrl(currentIdx)}
                alt={`Tattoo artwork ${currentIdx}`}
                className={styles.modalImage}
              />
              <button className={styles.modalNavRight} onClick={showNext} aria-label="Next">{'>'}</button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default Work;
